import { forwardRef } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import * as BaseCheckbox from '@radix-ui/react-checkbox';

import CheckmarkIcon from '#/assets/CheckmarkIcon';

import { c14142E, c1D1D37, c7A35CB, cFFFFFF } from '#/colors';

// Don't need to deal with "intermediate" state
type CheckedState = Exclude<BaseCheckbox.CheckedState, 'indeterminate'>;

interface Props
  extends React.ComponentPropsWithoutRef<typeof BaseCheckbox.Root> {
  // Override the checked state to not include "indeterminate"
  readonly checked?: CheckedState;
  readonly defaultChecked?: CheckedState;
  readonly onCheckedChange?: (checked: CheckedState) => void;
}

const Checkbox = forwardRef<React.ElementRef<typeof BaseCheckbox.Root>, Props>(
  function Checkbox(props, forwardedRef) {
    return (
      <CheckboxRoot {...props} ref={forwardedRef}>
        <BaseCheckbox.Indicator>
          <CheckmarkIcon size={10} thickness={2} />
        </BaseCheckbox.Indicator>
      </CheckboxRoot>
    );
  },
);

export default Checkbox;

const CheckboxRoot = styled(BaseCheckbox.Root)`
  width: 16px;
  height: 16px;
  line-height: 0;
  border: 1px solid ${c1D1D37};
  border-radius: 3px;
  background-color: ${c14142E};
  color: ${cFFFFFF};
  padding: 0;
  display: grid;
  align-items: center;
  justify-content: center;

  &:active {
    background-color: ${rgba(c7A35CB, 0.8)};
    outline: none;
  }

  &:hover {
    border: 1px solid ${rgba(c7A35CB, 0.5)};
  }

  &:focus-visible {
    outline: 2px solid ${rgba(c7A35CB, 0.5)};
    outline-offset: 2px;
  }

  &[data-state='checked'] {
    background-color: ${c7A35CB};
    border: none;
  }
`;
